import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ViewModal = ({ isOpen, onClose, data, activeTab }) => {
  if (!isOpen) return null;
  
  if (!data) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1001 p-4">
        <div className="bg-card border border-border rounded-lg p-6 max-w-md">
          <h2 className="text-lg font-semibold mb-4">No Data Available</h2>
          <p className="text-muted-foreground mb-4">
            The requested information could not be loaded.
          </p>
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }


  const InfoItem = ({ label, value, className = '' }) => (
  <div className={className}>
    <label className="block text-xs font-medium text-muted-foreground">{label}</label>
    <p className="mt-1 text-foreground text-sm">{value ?? 'N/A'}</p>
  </div>
);


  const getTabTitle = () => {
    switch (activeTab) {
      case 'digital-print-operators':
        return 'Digital Print Operator Details';
      case 'cutting-specialists':
        return 'Cutting Specialist Details';
      case 'raw-material-suppliers':
        return 'Raw Material Supplier Details';
      case 'clients':
        return 'Client Details';
      default:
        return 'Party Details';
    }
  };

  const renderContent = () => {
    if (!data) return null;

    switch (activeTab) {
      case 'digital-print-operators':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoItem label="Operator Name" value={data.name} />
              <InfoItem label="Code" value={data.code} />
              <InfoItem label="Specialization" value={data.specialization?.replace('-', ' ')?.toUpperCase()} />
              <InfoItem label="Rate per Meter" value={`₹${data.rate?.toLocaleString('en-IN')}`} />
              <InfoItem label="Experience" value={`${data.experience} years`} />
              <InfoItem 
                label="Status" 
                value={data.status} 
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  data.status === 'active' ? 'bg-success/10 text-success' :
                  data.status === 'inactive' ? 'bg-error/10 text-error' : 'bg-warning/10 text-warning'
                }`}
              />
            </div>
            
            <div className="border-t border-border pt-4">
              <h4 className="font-medium text-foreground mb-3">Contact Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoItem label="Phone" value={data.contact?.phone} />
                <InfoItem label="Email" value={data.contact?.email} />
              </div>
              <InfoItem label="Address" value={data.contact?.address} className="mt-4" />
            </div>
          </div>
        );

      case 'cutting-specialists':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoItem label="Specialist Name" value={data.name} />
              <InfoItem label="Code" value={data.code} />
              <InfoItem label="Expertise" value={data.expertise?.replace('-', ' ')?.toUpperCase()} />
              <InfoItem label="Rate per Piece" value={`₹${data.rate?.toLocaleString('en-IN')}`} />
              <InfoItem label="Experience" value={`${data.experience} years`} />
              <InfoItem 
                label="Status" 
                value={data.status}
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  data.status === 'active' ? 'bg-success/10 text-success' :
                  data.status === 'inactive' ? 'bg-error/10 text-error' : 'bg-warning/10 text-warning'
                }`}
              />
            </div>
            
            <div className="border-t border-border pt-4">
              <h4 className="font-medium text-foreground mb-3">Contact Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoItem label="Phone" value={data.contact?.phone} />
                <InfoItem label="Email" value={data.contact?.email} />
              </div>
              <InfoItem label="Address" value={data.contact?.address} className="mt-4" />
            </div>
          </div>
        );

      case 'raw-material-suppliers':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoItem label="Supplier Name" value={data.name} />
              <InfoItem label="Code" value={data.code} />
              <InfoItem label="KG Rate" value={`₹${data.kgRate?.toLocaleString('en-IN')}`} />
              <InfoItem label="Meter Rate" value={`₹${data.meterRate?.toLocaleString('en-IN')}`} />
              <InfoItem label="Conversion Ratio" value={`1 KG = ${data.conversionRatio} Meters`} />
              <InfoItem label="Payment Terms" value={data.paymentTerms} />
            </div>
            
            <div>
              <h4 className="font-medium text-foreground mb-3">Materials Supplied</h4>
              <div className="flex flex-wrap gap-2">
                {data.materials?.map((material, index) => (
                  <span key={index} className="px-3 py-1 bg-accent/10 text-accent rounded-full text-sm">
                    {material.charAt(0).toUpperCase() + material.slice(1)}
                  </span>
                ))}
              </div>
            </div>
            
            <div className="border-t border-border pt-4">
              <h4 className="font-medium text-foreground mb-3">Contact Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoItem label="Phone" value={data.contact?.phone} />
                <InfoItem label="Email" value={data.contact?.email} />
              </div>
              <InfoItem label="Address" value={data.contact?.address} className="mt-4" />
            </div>
          </div>
        );

      case 'clients':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoItem label="Client Name" value={data.name} />
              <InfoItem label="Code" value={data.code} />
              <InfoItem label="Online Rate" value={`₹${data.onlineRate?.toLocaleString('en-IN')}`} />
              <InfoItem label="Offline Rate" value={`₹${data.offlineRate?.toLocaleString('en-IN')}`} />
              <InfoItem label="Payment Terms" value={data.paymentTerms} />
              <InfoItem label="Credit Limit" value={`₹${data.creditLimit?.toLocaleString('en-IN')}`} />
            </div>
            
            <div>
              <h4 className="font-medium text-foreground mb-3">Platforms</h4>
              <div className="flex flex-wrap gap-2">
                {data.platforms?.map((platform, index) => (
                  <span key={index} className="px-3 py-1 bg-accent/10 text-accent rounded-full text-sm">
                    {platform}
                  </span>
                ))}
              </div>
            </div>
            
            <div className="border-t border-border pt-4">
              <h4 className="font-medium text-foreground mb-3">Contact Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoItem label="Phone" value={data.contact?.phone} />
                <InfoItem label="Email" value={data.contact?.email} />
              </div>
              <InfoItem label="Address" value={data.contact?.address} className="mt-4" />
            </div>
          </div>
        );

      case 'purchase-invoices':
        return (
          <div className="space-y-6">
            {/* Invoice Header */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoItem label="Invoice Number" value={data.invoiceNumber} />
              <InfoItem label="Status" 
                value={data.status}
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  data.status === 'paid' ? 'bg-success/10 text-success' :
                  data.status === 'pending' ? 'bg-warning/10 text-warning' :
                  data.status === 'overdue' ? 'bg-error/10 text-error' : 'bg-muted/10 text-muted-foreground'
                }`}
              />
              <InfoItem label="Invoice Date" value={new Date(data.invoiceDate).toLocaleDateString('en-IN')} />
              <InfoItem label="Due Date" value={new Date(data.dueDate).toLocaleDateString('en-IN')} />
              <InfoItem label="Payment Terms" value={data.paymentTerms} />
            </div>

            {/* Supplier Information */}
            <div className="border-t border-border pt-4">
              <h4 className="font-medium text-foreground mb-3">Supplier Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoItem label="Supplier Name" value={data.supplier?.name} />
                <InfoItem label="Supplier Code" value={data.supplier?.code} />
              </div>
            </div>

            {/* Invoice Items */}
            <div className="border-t border-border pt-4">
              <h4 className="font-medium text-foreground mb-3">Invoice Items</h4>
              <div className="bg-muted/25 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-foreground">Material</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-foreground">Quantity</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-foreground">Rate (₹)</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-foreground">Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items?.map((item, index) => (
                      <tr key={index} className="border-t border-border">
                        <td className="px-4 py-2 text-sm text-foreground">{item.material}</td>
                        <td className="px-4 py-2 text-sm text-foreground">{item.quantity}</td>
                        <td className="px-4 py-2 text-sm text-foreground">₹{item.rate?.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-2 text-sm text-foreground">₹{item.amount?.toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="border-t border-border pt-4">
              <h4 className="font-medium text-foreground mb-3">Financial Summary</h4>
              <div className="bg-muted/25 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InfoItem label="Subtotal" value={`₹${data.subtotal?.toLocaleString('en-IN')}`} />
                  <InfoItem label={`Tax (${data.taxRate}%)`} value={`₹${data.taxAmount?.toLocaleString('en-IN')}`} />
                  <InfoItem 
                    label="Total Amount" 
                    value={`₹${data.totalAmount?.toLocaleString('en-IN')}`}
                    className="text-lg font-bold text-primary"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            {data.notes && (
              <div className="border-t border-border pt-4">
                <InfoItem label="Notes" value={data.notes} />
              </div>
            )}

            {/* Timestamps */}
            <div className="border-t border-border pt-4 text-xs text-muted-foreground">
              <p>Created: {new Date(data.createdAt).toLocaleString('en-IN')}</p>
              {data.updatedAt && data.updatedAt !== data.createdAt && (
                <p>Updated: {new Date(data.updatedAt).toLocaleString('en-IN')}</p>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoItem label="Party Name" value={data.name} />
              <InfoItem label="Code" value={data.code} />
              <InfoItem label="Type" value={data.type} />
              <InfoItem label="Description" value={data.description} />
              <InfoItem 
                label="Status" 
                value={data.status}
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  data.status === 'active' ? 'bg-success/10 text-success' :
                  data.status === 'inactive' ? 'bg-error/10 text-error' : 'bg-warning/10 text-warning'
                }`}
              />
            </div>
            
            <div className="border-t border-border pt-4">
              <h4 className="font-medium text-foreground mb-3">Contact Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoItem label="Phone" value={data.contact?.phone} />
                <InfoItem label="Email" value={data.contact?.email} />
              </div>
              <InfoItem label="Address" value={data.contact?.address} className="mt-4" />
            </div>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1001 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-primary">
                {data.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{getTabTitle()}</h2>
              <p className="text-sm text-muted-foreground">{data.code}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          />
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-md font-medium text-foreground mb-4 flex items-center">
                <Icon name="User" size={18} className="mr-2" />
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Name</label>
                  <p className="text-foreground mt-1 font-medium">{data.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Code</label>
                  <p className="text-foreground mt-1">{data.code}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                    data.status === 'active' ? 'bg-success/10 text-success' :
                    data.status === 'inactive'? 'bg-error/10 text-error' : 'bg-warning/10 text-warning'
                  }`}>
                    {data.status?.charAt(0).toUpperCase() + data.status?.slice(1)}
                  </span>
                </div>
              </div>
            </div>

            {/* Specific Details */}
            <div>
              <h3 className="text-md font-medium text-foreground mb-4 flex items-center">
                <Icon name="Settings" size={18} className="mr-2" />
                Specific Details
              </h3>
              {renderContent()}
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="text-md font-medium text-foreground mb-4 flex items-center">
                <Icon name="Phone" size={18} className="mr-2" />
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Phone</label>
                  <p className="text-foreground mt-1">{data.contact?.phone || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Email</label>
                  <p className="text-foreground mt-1">{data.contact?.email || 'N/A'}</p>
                </div>
              </div>
              <div className="mt-4">
                <label className="text-sm font-medium text-muted-foreground">Address</label>
                <p className="text-foreground mt-1">{data.contact?.address || 'N/A'}</p>
              </div>
            </div>

            {/* Timestamps */}
            <div>
              <h3 className="text-md font-medium text-foreground mb-4 flex items-center">
                <Icon name="Clock" size={18} className="mr-2" />
                Record Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created Date</label>
                  <p className="text-foreground mt-1">
                    {data.createdAt ? new Date(data.createdAt).toLocaleDateString('en-IN') : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
                  <p className="text-foreground mt-1">
                    {data.updatedAt ? new Date(data.updatedAt).toLocaleDateString('en-IN') : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ViewModal;